<script setup>
import {computed, onBeforeUnmount, ref} from 'vue'
import helpers from '@/lib/helpers'
import {fileIcon, scanLine} from './file-kind'

const props = defineProps({
  // the lookup match's url: /api/file/{id}/meta
  url: String,
  // told when the file was read again (a file list refreshes)
  onChange: Function,
})

const meta = ref(null)
const blobUrl = ref(null)
const failed = ref(false)
const tab = ref('preview')
const text = ref(null)
const reading = ref(false)

const mime = computed(() => meta.value?.mime_type || '')
const kind = computed(() => {
  if (meta.value?.is_image || mime.value.startsWith('image/')) return 'image'
  if (mime.value === 'application/pdf') return 'pdf'
  if (mime.value.startsWith('text/')) return 'text'
  return 'other'
})
const scan = computed(() => meta.value?.scan)
// what the scans read: a document's text, a photo's (labels, serials)
const hasText = computed(() => scan.value && (scan.value.has_text || scan.value.kind === 'photo'))

async function load() {
  try {
    meta.value = await helpers.asyncAction(props.url, 'GET', {noload: true})
    // a Word / Excel file cannot be shown in the browser: its text can
    if (kind.value === 'other') {
      if (hasText.value)
        showText()
      return
    }
    const data = await helpers.asyncAction(`/api/file/${meta.value.id}`, 'GET', {responseType: 'blob'})
    blobUrl.value = URL.createObjectURL(new Blob([data], {type: mime.value}))
  } catch (e) {
    failed.value = true
  }
}

async function showText() {
  tab.value = 'text'
  if (text.value === null)
    text.value = await helpers.asyncAction(meta.value._links.text_url.href, 'GET', {noload: true})
}

async function readAgain() {
  if (reading.value)
    return
  reading.value = true
  try {
    const r = await helpers.asyncAction(meta.value._links.rescan_url.href, meta.value._links.rescan_url.method)
    meta.value.scan = r.scan
    text.value = null
    if (tab.value === 'text')
      await showText()
    props.onChange?.()
    helpers.toast(r.scan.status === 'read' ? 'Read again' : 'Could not be read: ' + (r.scan.error || 'no text'))
  } finally {
    reading.value = false
  }
}

load()

onBeforeUnmount(() => {
  if (blobUrl.value) URL.revokeObjectURL(blobUrl.value)
})

const openTab = () => window.open(blobUrl.value || `/api/file/${meta.value.id}`, '_blank')
const download = () => helpers.download(`/api/file/${meta.value.id}`, meta.value.orig_filename)
</script>

<template>
  <modal :title="meta ? meta.orig_filename : 'File'" large no-scroll>
    <template #footer>
      <div class="d-flex w-100 align-items-center">
        <b-button v-if="meta && meta._links.rescan_url" variant="outline-secondary" size="sm" class="px-3" :disabled="reading" @click="readAgain"
                  title="Read the file again with the scan">
          {{ reading ? 'Reading…' : 'Read again' }}
        </b-button>
        <b-button v-if="meta" variant="outline-secondary" size="sm" class="px-3 ml-auto mr-2" @click="download">Download</b-button>
        <b-button v-if="meta" variant="primary" size="sm" class="px-3" @click="openTab">Open in New Tab</b-button>
      </div>
    </template>

    <!-- a set height: the modal body only has a minimum, so a percentage left the preview at an iframe's 150px -->
    <div class="view-wrap d-flex flex-column">
      <div v-if="failed" class="m-3 text-danger">Could not load the file.</div>

      <div v-else-if="!meta" class="m-3 text-muted">Loading...</div>

      <template v-else>
        <!-- what the scan read about it -->
        <div v-if="scan" class="scan-strip d-flex align-items-start px-3 py-2 border-bottom">
          <font-awesome-icon :icon="fileIcon(meta).icon" :class="'text-' + fileIcon(meta).color + ' mr-2 mt-1'" size="lg"/>
          <div class="flex-fill min-w-0">
            <div class="d-flex align-items-center flex-wrap">
              <span v-if="scan.doc_type_desc" class="badge badge-info mr-2">{{ scan.doc_type_desc }}</span>
              <span v-if="scan.summary">{{ scan.summary }}</span>
              <span v-else-if="scan.status === 'pending'" class="text-muted font-italic">Not read yet - it is read a minute or so after it is added.</span>
              <span v-else-if="scan.status === 'failed'" class="text-muted font-italic">No text could be read ({{ scan.error }}).</span>
            </div>
            <div v-if="scanLine(meta)" class="small text-muted">{{ scanLine(meta) }}</div>
            <div v-if="scan.status === 'partial'" class="small text-muted font-italic">Only its text was read: {{ scan.error }}</div>
          </div>
          <b-button-group v-if="hasText && kind !== 'other'" size="sm" class="ml-2 flex-shrink-0">
            <b-button :variant="tab === 'preview' ? 'secondary' : 'outline-secondary'" @click="tab = 'preview'">Preview</b-button>
            <b-button :variant="tab === 'text' ? 'secondary' : 'outline-secondary'" @click="showText">Text</b-button>
          </b-button-group>
        </div>

        <div v-if="tab === 'text'" class="flex-fill overflow-auto p-3">
          <div v-if="text === null" class="text-muted">Loading...</div>
          <template v-else>
            <pre v-if="text.text" class="scan-text mb-0">{{ text.text }}</pre>
            <div v-else class="text-muted">No text was read from this file.</div>
            <div v-if="text.truncated" class="small text-muted mt-2">The text goes on - download the file for all of it.</div>
          </template>
        </div>

        <div v-else-if="kind === 'image'" class="flex-fill overflow-auto text-center p-2">
          <img v-if="blobUrl" :src="blobUrl" :alt="meta.orig_filename" style="max-width: 100%"/>
        </div>

        <iframe v-else-if="kind === 'pdf' || kind === 'text'" :src="blobUrl" class="flex-fill border-0 w-100 view-frame"/>

        <div v-else class="m-3 text-muted">
          This file type ({{ meta.file_type || mime || 'unknown' }}) can't be previewed here. Use Download or Open in New Tab.
        </div>

        <div class="small text-muted px-3 py-1 border-top">
          {{ meta.gli }} - added {{ meta.created_datetime }}<template v-if="meta.createdby_user"> by {{ meta.createdby_user.userid }}</template>
          <template v-if="meta.file_size"> - {{ meta.file_size | prettyBytes(0) }}</template>
        </div>
      </template>
    </div>
  </modal>
</template>

<style scoped>
.view-wrap {
  height: min(80vh, 900px);
}

.view-frame {
  min-height: 0;
}

.min-w-0 {
  min-width: 0;
}

.scan-text {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: .8rem;
}
</style>
